import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useMode } from '../contexts/ModeContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Menu, X, User } from 'lucide-react';
import LanguageSelector from './LanguageSelector';

const Header = () => {
  const { mode, setMode, isLabs } = useMode();
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = isLabs 
    ? [{ label: t('header.services'), href: '#services' }, { label: t('header.contact'), href: '#contact' }]
    : [{ label: t('header.pricing'), href: '#pricing' }, { label: t('header.faq'), href: '#faq' }];

  return (
    <header className={`header transition-all duration-300 ${scrolled ? 'bg-black/70 backdrop-blur-lg shadow-xl' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo - Taille native optimisée */}
        <Link to="/" className="flex-shrink-0">
          <motion.img 
            key={mode}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            src={isLabs 
              ? "/labs-logo.png"
              : "/callup-logo.png"
            }
            alt={isLabs ? "Labs Agency" : "Call Up"}
            className="h-auto max-h-12 sm:max-h-14 md:max-h-16 w-auto"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <a 
              key={item.label}
              href={item.href} 
              className={`text-sm font-semibold transition-colors ${isLabs ? 'text-gray-600 hover:text-black' : 'text-gray-400 hover:text-white'}`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-3">
          {/* Language Selector (Desktop only) */}
          <div className="hidden md:block">
            <LanguageSelector />
          </div>

          {/* Mode Switch */}
          <div className="switch-container header-switch">
            <motion.div 
              className="switch-pill"
              style={{ background: isLabs ? 'linear-gradient(135deg, #FB923C, #F59E0B)' : 'linear-gradient(135deg, #38BDF8, #A855F7)' }}
              animate={{ 
                left: isLabs ? '4px' : '50%',
                width: 'calc(50% - 4px)'
              }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            />
            <motion.button 
              className={`switch-btn ${isLabs ? 'active' : ''}`}
              onClick={() => {
                setMode('labs');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              Agency
            </motion.button>
            <motion.button 
              className={`switch-btn ${!isLabs ? 'active' : ''}`}
              onClick={() => {
                setMode('callup');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              Call Up
            </motion.button>
          </div>

          {/* Auth */}
          {isAuthenticated ? (
            <button 
              onClick={() => navigate('/dashboard')}
              className={`hidden sm:flex items-center gap-2 px-3 py-2 rounded-full transition-colors ${isLabs ? 'bg-gray-100 hover:bg-gray-200' : 'bg-white/10 hover:bg-white/20'}`}
            >
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
              ) : (
                <User className="w-5 h-5" />
              )}
            </button>
          ) : (
            <Link 
              to="/login" 
              className={`hidden sm:block text-sm font-semibold px-4 py-2 rounded-full transition-colors ${isLabs ? 'bg-gray-100 hover:bg-gray-200 text-gray-800' : 'bg-white/10 hover:bg-white/20 text-white'}`}
            >
              {t('header.login')}
            </Link>
          )}

          {/* Mobile menu */}
          <button 
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden mt-4 pb-4 border-t border-current/10"
          >
            <div className="pt-4 space-y-3">
              {navItems.map((item) => (
                <a 
                  key={item.label}
                  href={item.href}
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 font-semibold"
                >
                  {item.label}
                </a>
              ))}
              
              {isAuthenticated ? (
                <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="block py-2 font-semibold">
                  {t('header.dashboard')}
                </Link>
              ) : (
                <Link to="/login" onClick={() => setMenuOpen(false)} className="block py-2 font-semibold">
                  {t('header.login')}
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
